<template>
    <div class="stf-autocomplete" ref="dropdownContainerRef">
        <label v-if="label.length !== 0" class="trademark-input-label">
            {{ label }}
            <span v-if="required" class="required-indicator">*</span>
        </label>
        <div class="stf-dropdown-input-container">
            <StfInput
                :model-value="modelValue"
                :placeholder="placeholder"
                :required="required"
                :icon="icon"
                :success="success"
                :error="error"
                no-error-message
                @update:modelValue="onInputChange"
                @keydown.enter.prevent="selectFirstOption"
                @focus="onFocus"
            />
            <div class="stf-dropdown-arrow">
                <slot name="happen">
                    <StfIcon icon="chevronDown" size="small" />
                </slot>
            </div>


        </div>
        <div v-if="error && !isOpen" class="stf-input-error">{{ error }}</div>
        <div class="stf-dropdown-options-container">
            <ul
                v-show="isOpen"
                class="stf-dropdown-options"
            >
        
                <template v-for="(option, index) in options" :key="index">
                    <li
                        class="stf-dropdown-loading"
                        v-if="isLoading(option)"
                    >
                        <div class="stf-dropdown-loading">
                            <div class="spinner"></div>
                        </div>
                    </li>
                    <li
                        v-else-if="isSeparator(option)"
                        class="stf-dropdown-separator"
                    ></li>
    
                    <li
                        v-else-if="isDropdownOption(option)"
                        class="stf-dropdown-option"
                        :class="{ 'selected': modelValue === option.value, 'stf-dropdown-option--info': option.type === 'info' }"
                        @click="selectOption(option)"
                    >
                        <div v-if="option.icon" class="stf-dropdown-option-icon">
                            <StfIcon :icon="option.icon" size="small" />
                        </div>
                        <span class="stf-dropdown-option-text">{{ option.label }}</span>
                    </li>  
                </template>
            </ul>
        </div>
        
    </div>
</template>
  
<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import type { IconName } from '@/ui/icon/icons';
import StfInput from '@/ui/input/StfInput.vue';
import StfIcon from '@/ui/icon/StfIcon.vue';
import type { DropdownItem, DropdownOption, loadingOption, SeparatorOption } from '@/ui/dropdown/StfDropdown';

interface Props {
    modelValue: string;
    options: DropdownItem[];
    label?: string;
    placeholder?: string;
    icon?: IconName;
    required?: boolean;
    error?: string | null;
    success?: boolean;
    openOnFocus?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    options: () => [],
    modelValue: '',
    placeholder: 'Select an option',
    label: '',
    required: false,
    success: false,
    error: null,
    openOnFocus: false,
})


const isOpen = ref(false)
const dropdownContainerRef = ref<HTMLElement | null>(null);

const emit = defineEmits(['update:modelValue', 'update:selected']);

const onInputChange = (value: string) => {
    emit('update:modelValue', value);
    isOpen.value = true
}

const onFocus = () => {
    if (props.openOnFocus) {
        isOpen.value = true
    }
}

const selectFirstOption = () => {
    const isLoading = props.options.length === 1 && (props.options[0] as loadingOption).type === 'loading';

    if (isLoading) {
        return;
    }

    if (props.options.length > 0) {
        selectOption(props.options[0] as DropdownOption);
    }
};

const selectOption = (option: DropdownOption) => {
    if (option.type === 'info') {
        return;
    }
    emit('update:selected', option.value);
    isOpen.value = false;
};

const isSeparator = (option: DropdownItem): option is SeparatorOption => {
    return 'type' in option && option.type === 'separator';
};

const isLoading = (option: DropdownItem): option is loadingOption => {
    return 'type' in option && option.type === 'loading';
};

const isDropdownOption = (option: DropdownItem): option is DropdownOption => {
    return !('type' in option) || option.type === 'info';
};

const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (isOpen.value &&
        !dropdownContainerRef.value?.contains(target)) {
        isOpen.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

$background-color: #fff;

.stf-autocomplete {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.trademark-input-label {
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-14;
  font-weight: vars.$font-weight-semibold;
  line-height: 1.5;
  width: 100%;
}

.required-indicator {
  color: #ff3b30;
  margin-left: 2px;
}

.stf-dropdown-input-container {
  position: relative;
}

.stf-dropdown-arrow {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 1;
  height: 100%;
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
  border-right: 1px solid transparent;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  width: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stf-dropdown-options-container {
  position: relative;
  margin-top: -4px;
}

.stf-dropdown-options {
  position: absolute;
  z-index: 1;
  width: 100%;
  overflow-y: auto;
  background-color: $background-color;
  border: 1px solid vars.$color-gray-300;
  border-radius: 4px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  max-height: 300px;
}

.stf-dropdown-option {
  padding: 8px 12px;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-13;
  line-height: 1.5;
  color: vars.$color-black;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: darken(#E5E5E5, 5%);
  }

  &.selected {
    background-color: darken(#E5E5E5, 10%);
    font-weight: vars.$font-weight-semibold;
  }

  &--info {
    cursor: default;
    &:hover {
      background-color: transparent;
    }
  }

  &.stf-mobile-option {
    padding: 16px;
    font-size: vars.$font-size-16;
    border-bottom: 1px solid vars.$color-gray-200;

    &:hover {
      background-color: vars.$color-gray-100;
    }

    &.selected {
      background-color: vars.$color-gray-200;
      font-weight: vars.$font-weight-semibold;
    }
  }
}

.stf-dropdown-option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stf-dropdown-option-text {
  flex: 1;
  min-width: 0;
}

.stf-dropdown-separator {
  height: 1px;
  background-color: vars.$color-gray-300;
  margin: 4px 0;
}

.stf-dropdown-loading {
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: vars.$color-action;
  animation: spin 0.8s linear infinite;
}

.stf-input-error {
  color: vars.$color-error;
  font-family: vars.$font-family-primary;
  font-size: vars.$font-size-12;
  line-height: 1.4;
  margin-top: 2px;
}
</style>
